import { Matrix4, PerspectiveCamera } from "three";
import {
  float,
  getViewPosition,
  log2,
  max,
  mix,
  uniform,
  uv,
  vec3,
  vec4,
} from "three/tsl";
import { traa } from "three/examples/jsm/tsl/display/TRAANode.js";
import type {
  TslEffect,
  TslEffectContext,
} from "../../../../engine/postprocessing/PostProcessingController";
import type {
  SsgiSparseAdaptive,
  SsgiSparseEdgeKernel,
  SsgiSparseOutputMode,
} from "../../RenderSettingsStore";
import { sparseGi } from "./SparseGiNode.js";
import { GI_EDGE_KERNEL } from "./sparseGiDepthEdge";

function edgeKernelId(kernel: SsgiSparseEdgeKernel): number {
  return GI_EDGE_KERNEL[kernel];
}

export type SparseGiEffectOptions = {
  output?: SsgiSparseOutputMode;
  sliceCount?: number;
  stepCount?: number;
  radius?: number;
  thickness?: number;
  expFactor?: number;
  giIntensity?: number;
  backfaceLighting?: number;
  useTemporalFiltering?: boolean;
  useScreenSpaceSampling?: boolean;
  useLinearThickness?: boolean;
  giStride?: number;
  giAdaptive?: SsgiSparseAdaptive;
  giEdgeThickness?: number;
  giEdgeKernel?: SsgiSparseEdgeKernel;
  giEdgeThreshold?: number;
  giDepthThreshold?: number;
  giNormalThreshold?: number;
  giMinOkCount?: number;
};

function asColorNode(input: unknown): unknown {
  if (
    input &&
    typeof (input as { getTextureNode?: () => unknown }).getTextureNode ===
      "function"
  ) {
    return (input as { getTextureNode: () => unknown }).getTextureNode();
  }
  return input;
}

/**
 * Sparse GI only — no AO. Grid-stride SparseGiNode + 4-tap upsample.
 */
export class SparseGiTslEffect implements TslEffect {
  readonly order = 10;
  readonly branch = "main" as const;
  readonly needsNormal = true;
  readonly needsDiffuse = true;
  get needsVelocity(): boolean {
    return this._useTemporalFiltering;
  }

  private _output: SsgiSparseOutputMode;
  private _sliceCount: number;
  private _stepCount: number;
  private _radius: number;
  private _thickness: number;
  private _expFactor: number;
  private _giIntensity: number;
  private _backfaceLighting: number;
  private _useTemporalFiltering: boolean;
  private _useScreenSpaceSampling: boolean;
  private _useLinearThickness: boolean;
  private _giStride: number;
  private _giAdaptive: SsgiSparseAdaptive;
  private _giEdgeThickness: number;
  private _giEdgeKernel: SsgiSparseEdgeKernel;
  private _ssgiNode: ReturnType<typeof sparseGi> | undefined;
  private readonly _giStrideUniform = uniform(2);
  private readonly _giDepthThresholdUniform = uniform(0.25);
  private readonly _giEdgeThresholdUniform = uniform(0.04);
  private readonly _giNormalThresholdUniform = uniform(0.8);
  private readonly _giMinOkCountUniform = uniform(3);
  private readonly _projInvUniform = uniform(new Matrix4());

  constructor(options: SparseGiEffectOptions = {}) {
    this._output = options.output ?? "combined";
    this._sliceCount = options.sliceCount ?? 3;
    this._stepCount = options.stepCount ?? 8;
    this._radius = options.radius ?? 12;
    this._thickness = options.thickness ?? 1;
    this._expFactor = options.expFactor ?? 2;
    this._giIntensity = options.giIntensity ?? 10;
    this._backfaceLighting = options.backfaceLighting ?? 0;
    this._useTemporalFiltering = options.useTemporalFiltering ?? true;
    this._useScreenSpaceSampling = options.useScreenSpaceSampling ?? true;
    this._useLinearThickness = options.useLinearThickness ?? false;
    this._giStride = Math.min(
      64,
      Math.max(2, Math.floor(options.giStride ?? 2)),
    );
    this._giStrideUniform.value = this._giStride;
    this._giAdaptive = options.giAdaptive ?? "off";
    this._giEdgeThickness = Math.min(
      8,
      Math.max(0, Math.floor(options.giEdgeThickness ?? 2)),
    );
    this._giEdgeKernel = options.giEdgeKernel ?? "sobel";
    this._giEdgeThresholdUniform.value = options.giEdgeThreshold ?? 0.04;
    this._giDepthThresholdUniform.value = options.giDepthThreshold ?? 0.25;
    this._giNormalThresholdUniform.value = options.giNormalThreshold ?? 0.8;
    this._giMinOkCountUniform.value = Math.min(
      4,
      Math.max(1, Math.floor(options.giMinOkCount ?? 3)),
    );
  }

  setParameters(partial: SparseGiEffectOptions): boolean {
    let graphDirty = false;
    if (partial.output != null && partial.output !== this._output) {
      this._output = partial.output;
      graphDirty = true;
    }
    if (partial.sliceCount != null) this._sliceCount = partial.sliceCount;
    if (partial.stepCount != null) this._stepCount = partial.stepCount;
    if (partial.radius != null) this._radius = partial.radius;
    if (partial.thickness != null) this._thickness = partial.thickness;
    if (partial.expFactor != null) this._expFactor = partial.expFactor;
    if (partial.giIntensity != null) this._giIntensity = partial.giIntensity;
    if (partial.backfaceLighting != null)
      this._backfaceLighting = partial.backfaceLighting;
    if (
      partial.useTemporalFiltering != null &&
      partial.useTemporalFiltering !== this._useTemporalFiltering
    ) {
      this._useTemporalFiltering = partial.useTemporalFiltering;
      graphDirty = true;
    }
    if (partial.useScreenSpaceSampling != null)
      this._useScreenSpaceSampling = partial.useScreenSpaceSampling;
    if (partial.useLinearThickness != null)
      this._useLinearThickness = partial.useLinearThickness;
    if (partial.giStride != null) {
      const next = Math.min(64, Math.max(2, Math.floor(partial.giStride)));
      if (next !== this._giStride) {
        this._giStride = next;
        this._giStrideUniform.value = next;
        graphDirty = true;
      }
    }
    if (partial.giAdaptive != null && partial.giAdaptive !== this._giAdaptive) {
      this._giAdaptive = partial.giAdaptive;
      graphDirty = true;
    }
    if (partial.giEdgeThickness != null) {
      const next = Math.min(8, Math.max(0, Math.floor(partial.giEdgeThickness)));
      if (next !== this._giEdgeThickness) {
        this._giEdgeThickness = next;
      }
    }
    if (partial.giEdgeKernel != null && partial.giEdgeKernel !== this._giEdgeKernel) {
      this._giEdgeKernel = partial.giEdgeKernel;
    }
    if (partial.giEdgeThreshold != null) {
      this._giEdgeThresholdUniform.value = partial.giEdgeThreshold;
    }
    if (partial.giDepthThreshold != null) {
      this._giDepthThresholdUniform.value = partial.giDepthThreshold;
    }
    if (partial.giNormalThreshold != null) {
      this._giNormalThresholdUniform.value = partial.giNormalThreshold;
    }
    if (partial.giMinOkCount != null) {
      this._giMinOkCountUniform.value = Math.min(
        4,
        Math.max(1, Math.floor(partial.giMinOkCount)),
      );
    }
    this._syncNode();
    return graphDirty;
  }

  private _syncNode(): void {
    const node = this._ssgiNode;
    if (!node) return;
    node.sliceCount.value = this._sliceCount;
    node.stepCount.value = this._stepCount;
    node.radius.value = this._radius;
    node.thickness.value = this._thickness;
    node.expFactor.value = this._expFactor;
    node.giIntensity.value = this._giIntensity;
    node.backfaceLighting.value = this._backfaceLighting;
    node.useTemporalFiltering = this._useTemporalFiltering;
    node.useScreenSpaceSampling.value = this._useScreenSpaceSampling;
    node.useLinearThickness.value = this._useLinearThickness;
    node.giStride = this._giStride;
    node.adaptiveMode = this._giAdaptive;
    node.edgeThickness.value = this._giEdgeThickness;
    node.edgeKernel.value = edgeKernelId(this._giEdgeKernel);
    node.edgeThreshold.value = this._giEdgeThresholdUniform.value;
    node.depthThreshold.value = this._giDepthThresholdUniform.value;
    node.normalThreshold.value = this._giNormalThresholdUniform.value;
    node.minOkCount.value = this._giMinOkCountUniform.value;
    node.debugView.value = this._output === "debug" ? 1 : 0;
  }

  dispose(): void {
    (this._ssgiNode as { dispose?: () => void } | undefined)?.dispose?.();
    this._ssgiNode = undefined;
  }

  compose(input: unknown, ctx: TslEffectContext): unknown {
    const camera = ctx.camera;
    if (!(camera instanceof PerspectiveCamera)) {
      return input;
    }

    this._projInvUniform.value = camera.projectionMatrixInverse;

    const beauty = asColorNode(input);
    if (this._output === "direct") {
      return beauty;
    }

    if (this._output === "normal") {
      if (!ctx.normal) return beauty;
      const n = asColorNode(ctx.normal) as {
        rgb: { mul: (v: unknown) => { add: (v: unknown) => unknown } };
      };
      return vec4(n.rgb.mul(0.5).add(0.5) as never, 1);
    }

    if (this._output === "depth") {
      if (!ctx.depth) return beauty;
      const raw = (asColorNode(ctx.depth) as { r: unknown }).r;
      const z = (
        getViewPosition(
          uv() as never,
          raw as never,
          this._projInvUniform as never,
        ) as { z: { abs: () => unknown } }
      ).z.abs();
      const n = max(float(camera.near), float(0.05));
      const range = float(16);
      const t = log2(max(z as never, n) as never)
        .sub(log2(n))
        .div(max(log2(range).sub(log2(n)) as never, float(1e-4)) as never)
        .saturate();
      const r = float(1).sub(t as never);
      const oneMinusT = float(1).sub(t as never);
      const g = (
        t as {
          mul: (v: unknown) => { mul: (n: number) => { saturate: () => unknown } };
        }
      )
        .mul(oneMinusT)
        .mul(4)
        .saturate();
      return vec4(r as never, g as never, t as never, 1);
    }

    const normal = (ctx.normal ?? null) as never;
    const node = sparseGi(beauty as never, ctx.depth as never, normal, camera);
    this._ssgiNode = node;
    node.giStride = this._giStride;
    node.adaptiveMode = this._giAdaptive;
    this._syncNode();

    const tex = (
      node as unknown as {
        getTextureNode: () => { rgb: unknown };
      }
    ).getTextureNode();
    const gi = tex.rgb;

    if (this._output === "gi" || this._output === "debug") {
      return vec4(gi as never, 1);
    }

    const color = beauty as {
      rgb: { add: (v: unknown) => unknown };
      a: unknown;
    };
    const diffuseSrc = asColorNode(ctx.diffuse ?? beauty) as {
      rgb: { mul: (v: unknown) => unknown };
    };
    const litRgb = color.rgb.add(diffuseSrc.rgb.mul(gi));
    const rgb = mix(color.rgb as never, litRgb as never, color.a as never);
    const composite = vec4(rgb as never, color.a as never);

    if (this._useTemporalFiltering && ctx.depth && ctx.velocity) {
      const accumulated = traa(
        composite as never,
        ctx.depth as never,
        ctx.velocity as never,
        camera,
      ) as unknown as { rgb: unknown };
      const accRgb = mix(
        color.rgb as never,
        accumulated.rgb as never,
        color.a as never,
      );
      return vec4(accRgb as never, color.a as never);
    }
    return composite;
  }
}
